import { createContext, useCallback, useContext, useEffect, useMemo, useState, FC, ReactNode } from 'react';
import { useAppCookies } from './AppCookieProvider';

const STORAGE_PREFIX = 'whe.debugMode';

export interface DebugMode {
  /** Whether debug output should render right now. */
  enabled: boolean;
  /** Whether the toggle should be offered at all — development builds only. */
  available: boolean;
  setEnabled: (next: boolean) => void;
}

/** Off and not offerable. Used wherever no provider is mounted, e.g. unit tests. */
const DEFAULT: DebugMode = { enabled: false, available: false, setEnabled: () => undefined };

const DebugModeContext = createContext<DebugMode | undefined>(undefined);

function isDevBuild(): boolean {
  return import.meta.env.VITE_APP_ENV === 'development';
}

/**
 * Scope the flag to whoever is signed in, so switching it on for one account
 * doesn't switch it on for the next person to use the same browser.
 */
function storageKey(email?: string): string {
  return `${STORAGE_PREFIX}:${email || 'anonymous'}`;
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    // Storage can be unavailable (private browsing). Debug output just stays off.
    return false;
  }
}

/**
 * Opt-in visibility for `<DebugPanel>`.
 *
 * The panels dump the raw object behind whatever the UI is rendering, which is
 * useful while working on a screen and noise the rest of the time — it lands in
 * the DOM of a shared environment, where it leaks API payloads onto the page and
 * makes any text-based assertion ambiguous. So it is off unless someone turns it
 * on for themselves from the admin screen, and unavailable outside dev builds.
 */
export const DebugModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { profile } = useAppCookies();
  const available = isDevBuild();
  const key = storageKey((profile as { email?: string }).email);

  const [enabled, setEnabledState] = useState(() => available && readFlag(key));

  // Re-read when the signed-in user changes — the flag belongs to the account.
  useEffect(() => {
    setEnabledState(available && readFlag(key));
  }, [available, key]);

  const setEnabled = useCallback(
    (next: boolean) => {
      try {
        window.localStorage.setItem(key, String(next));
      } catch {
        // Not persisting is survivable; honour the toggle for this session.
      }
      setEnabledState(next);
    },
    [key]
  );

  const value = useMemo<DebugMode>(
    () => ({ enabled: available && enabled, available, setEnabled }),
    [available, enabled, setEnabled]
  );

  return <DebugModeContext.Provider value={value}>{children}</DebugModeContext.Provider>;
};

/** Never throws when unprovided — `<DebugPanel>` renders in trees that don't mount the provider. */
export function useDebugMode(): DebugMode {
  return useContext(DebugModeContext) ?? DEFAULT;
}
